<script lang="ts">
  import { MATERIAL_TYPE_ICONS } from '../lib/icons';
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import IconPicker from '../components/IconPicker.svelte';
  import type { IconName } from '../lib/icons';
  import MarkdownEditor from '../components/MarkdownEditor.svelte';
  import { renderMarkdown } from '../lib/markdown';

  /** Land on Content > Розділи instead of the default dashboard tab. */
  function backToCategories(): void {
    adminState.activeTab = 'content';
    adminState.contentSubTab = 'categories';
    push('/admin/dashboard');
  }
  import type { AdminCategoryDetailDTO, AdminCategoryDTO, MaterialType } from '../lib/types';

  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();
  const categoryId = $derived(params?.id ?? '');

  let loading = $state(true);
  let notFound = $state(false);
  let original = $state<AdminCategoryDetailDTO | null>(null);

  let icon = $state<IconName>('leaf');
  let name = $state('');
  let description = $state('');
  let isActive = $state(true);

  let saving = $state(false);
  let error = $state<string | null>(null);

  // Unpublish-with-materials warning modal — shown on Save, not on pill click.
  let unpublishConfirmOpen = $state(false);

  // Delete flow state
  let deleteModalOpen = $state(false);
  let deleting = $state(false);
  let deleteError = $state<string | null>(null);
  let otherCategories = $state<AdminCategoryDTO[] | null>(null);
  let moveTargetId = $state('');
  let moving = $state(false);
  // Second-step «final confirmation» inside the same modal.
  let deleteFinalStep = $state(false);

  
  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    if (!categoryId) {
      notFound = true;
      loading = false;
      return;
    }
    try {
      const c = await api.adminCategory(categoryId);
      original = c;
      icon = c.icon;
      name = c.name;
      description = c.description ?? '';
      isActive = c.isActive;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) notFound = true;
      else error = 'Не вдалось завантажити розділ';
    } finally {
      loading = false;
    }
  });

  const canSave = $derived(
    !loading &&
      !!original &&
      name.trim().length > 0 &&
      (icon !== original.icon ||
        name.trim() !== original.name ||
        (description.trim() || null) !== (original.description ?? null) ||
        isActive !== original.isActive),
  );

  async function save(): Promise<void> {
    if (!canSave || saving || !original) return;
    // Guard: deopublish of non-empty category needs explicit confirmation.
    if (original.isActive && !isActive && original.materials.length > 0) {
      unpublishConfirmOpen = true;
      return;
    }
    await doSave();
  }

  async function doSave(): Promise<void> {
    if (!original) return;
    saving = true;
    error = null;
    try {
      await api.adminPatchCategory(categoryId, {
        icon,
        name: name.trim(),
        description: description.trim() || null,
        isActive,
      });
      backToCategories();
    } catch (err) {
      error = err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }

  async function confirmUnpublishAndSave(): Promise<void> {
    unpublishConfirmOpen = false;
    await doSave();
  }
  function cancelUnpublish(): void {
    unpublishConfirmOpen = false;
  }

  /** Either deletes the category outright OR opens the «move materials first» modal. */
  async function tryDelete(): Promise<void> {
    if (!original) return;
    if (original.materials.length === 0) {
      if (!confirm(`Видалити розділ «${original.name}»?`)) return;
      await doDelete();
      return;
    }
    // Has materials — load other categories for the move modal.
    otherCategories = null;
    moveTargetId = '';
    deleteError = null;
    deleteFinalStep = false;
    deleteModalOpen = true;
    try {
      const r = await api.adminCategories();
      otherCategories = r.items.filter((c) => c.id !== categoryId);
      if (otherCategories.length > 0) moveTargetId = otherCategories[0].id;
    } catch {
      otherCategories = [];
    }
  }

  async function doDelete(): Promise<void> {
    deleting = true;
    deleteError = null;
    try {
      await api.adminDeleteCategory(categoryId);
      backToCategories();
    } catch (err) {
      deleteError = err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось видалити';
    } finally {
      deleting = false;
    }
  }

  async function moveAndDelete(): Promise<void> {
    if (!moveTargetId || moving) return;
    moving = true;
    deleteError = null;
    try {
      await api.adminMoveCategoryMaterials(categoryId, moveTargetId);
      // Now empty — delete the category.
      await api.adminDeleteCategory(categoryId);
      backToCategories();
    } catch (err) {
      deleteError =
        err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось перенести/видалити';
      moving = false;
    }
  }

  // Live preview of unsaved form values, rendered as the user would see it.
  let previewOpen = $state(false);
  function openPreview(): void { previewOpen = true; }
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">Редагувати розділ</div>
    {#if original && !loading && !notFound}
      <button
        type="button"
        class="header-preview"
        onclick={openPreview}
        aria-label="Перегляд"
        title="Перегляд"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </button>
      <button
        type="button"
        class="header-delete"
        disabled={deleting}
        onclick={tryDelete}
        aria-label="Видалити розділ"
        title="Видалити розділ"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      </button>
    {:else}
      <span></span>
    {/if}
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if notFound}
      <div class="placeholder">Розділ не знайдено</div>
    {:else if original}
      <label class="field-label">Емодзі та назва</label>
      <div class="icon-name-row">
        <IconPicker bind:value={icon} />
        <input class="field name-input" maxlength="80" bind:value={name} />
      </div>

      <label class="field-label">Опис</label>
      <MarkdownEditor bind:value={description} maxlength={800} />

      <label class="field-label">Стан</label>
      <div class="status-pills">
        <button
          type="button"
          class="status-pill"
          class:active={!isActive}
          onclick={() => (isActive = false)}
        >Чернетка</button>
        <button
          type="button"
          class="status-pill"
          class:active={isActive}
          onclick={() => (isActive = true)}
        >Опубліковано</button>
      </div>

      <div class="info-line">
        Матеріалів у розділі: <strong>{original.materials.length}</strong>
      </div>

      {#if error}<div class="error">{error}</div>{/if}

      <button
        type="button"
        class="submit"
        disabled={!canSave || saving}
        onclick={save}
      >{saving ? 'Зберігаємо…' : 'Зберегти'}</button>
    {/if}
  </div>

  {#if unpublishConfirmOpen && original}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={cancelUnpublish}></div>
    <div class="modal-sheet modal-confirm" role="dialog" aria-modal="true">
      <div class="modal-head">
        <div class="modal-title">Деопублікувати розділ?</div>
        <button
          type="button"
          class="modal-close"
          onclick={cancelUnpublish}
        ><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <p class="modal-desc">
          У розділі <strong>original.name}</strong> зараз
          <strong>{original.materials.length}</strong>
          {original.materials.length === 1 ? 'матеріал' : original.materials.length < 5 ? 'матеріали' : 'матеріалів'}.
        </p>
        <p class="modal-desc warn">
          Якщо переведеш розділ у чернетку, користувачі не зможуть бачити
          його в каталозі і не матимуть доступу до матеріалів.
        </p>
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          onclick={cancelUnpublish}
        >Скасувати</button>
        <button
          type="button"
          class="modal-btn modal-btn-warn"
          disabled={saving}
          onclick={confirmUnpublishAndSave}
        >{saving ? 'Зберігаємо…' : 'Деопублікувати і зберегти'}</button>
      </div>
    </div>
  {/if}

  {#if previewOpen && original}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => (previewOpen = false)}></div>
    <div class="preview-sheet" role="dialog" aria-modal="true" aria-label="Перегляд">
      <div class="modal-head">
        <div class="modal-title">Перегляд для користувача</div>
        <button type="button" class="modal-close" onclick={() => (previewOpen = false)}><Icon name="x" /></button>
      </div>
      <div class="modal-body preview-page">
        <div class="preview-topbar">
          <Icon name={icon} size="lg" /> {name.trim() || '(без назви)'}
        </div>
        <div class="preview-content">
          {#if description.trim()}
            <div class="promo">
              <div class="promo-text md-content">{@html renderMarkdown(description)}</div>
            </div>
          {/if}

          <div class="preview-state-row">
            <span class="preview-state-pill" class:pub={isActive}>
              {#if isActive}<Icon name="check" size="xs" /> Опубліковано{:else}◌ Чернетка{/if}
            </span>
            <span class="preview-state-pill neutral">
              {original.materials.length} {original.materials.length === 1 ? 'матеріал' : original.materials.length < 5 ? 'матеріали' : 'матеріалів'}
            </span>
          </div>

          {#if original.materials.length > 0}
            <div class="materials">
              {#each original.materials as m (m.id)}
                <div class="material">
                  <div class="material-icon"><Icon name={MATERIAL_TYPE_ICONS[m.type]} /></div>
                  <div class="material-text">
                    <div class="material-title">{m.title}</div>
                    {#if !m.isPublished}<span class="material-draft">чернетка</span>{/if}
                  </div>
                  <div class="material-chevron">›</div>
                </div>
              {/each}
            </div>
          {:else}
            <div class="preview-empty">Скоро тут будуть матеріали</div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  {#if deleteModalOpen && original}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => (deleteModalOpen = false)}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true">
      {#if !deleteFinalStep}
        <!-- Step 1: select target category -->
        <div class="modal-head">
          <div class="modal-title">Спочатку перенесіть матеріали</div>
          <button
            type="button"
            class="modal-close"
            onclick={() => (deleteModalOpen = false)}
          ><Icon name="x" /></button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            У розділі <strong>original.name}</strong> ще
            <strong>{original.materials.length}</strong>
            {original.materials.length === 1 ? 'матеріал' : original.materials.length < 5 ? 'матеріали' : 'матеріалів'}.
            Виберіть розділ, куди їх перенести:
          </p>

          <ul class="mat-list">
            {#each original.materials as m (m.id)}
              <li class="mat-item">
                <span class="mat-icon"><Icon name={MATERIAL_TYPE_ICONS[m.type]} size="xs" /></span>
                <span class="mat-title">{m.title}</span>
                {#if !m.isPublished}<span class="draft-pill">чернетка</span>{/if}
              </li>
            {/each}
          </ul>

          <label class="field-label">Перенести в розділ</label>
          {#if otherCategories === null}
            <div class="placeholder small">Завантажуємо розділи…</div>
          {:else if otherCategories.length === 0}
            <div class="placeholder small">Немає інших розділів — спочатку створіть один</div>
          {:else}
            <select class="field" bind:value={moveTargetId}>
              {#each otherCategories as c (c.id)}
                <option value={c.id}>{c.name}</option>
              {/each}
            </select>
          {/if}
        </div>
        <div class="modal-actions">
          <button
            type="button"
            class="modal-btn modal-btn-secondary"
            onclick={() => (deleteModalOpen = false)}
          >Скасувати</button>
          <button
            type="button"
            class="modal-btn modal-btn-primary"
            disabled={!moveTargetId}
            onclick={() => (deleteFinalStep = true)}
          >Далі</button>
        </div>
      {:else}
        <!-- Step 2: final irreversible confirmation -->
        {@const target = otherCategories?.find((c) => c.id === moveTargetId)}
        <div class="modal-head">
          <div class="modal-title">Підтвердіть видалення</div>
          <button
            type="button"
            class="modal-close"
            disabled={moving}
            onclick={() => (deleteModalOpen = false)}
          ><Icon name="x" /></button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            Усі <strong>{original.materials.length}</strong>
            {original.materials.length === 1 ? 'матеріал буде перенесений' : original.materials.length < 5 ? 'матеріали будуть перенесені' : 'матеріалів буде перенесено'}
            у розділ
            <strong>{target ? target.name : '—'}</strong>,
            а розділ <strong>original.name}</strong> буде видалений.
          </p>
          <p class="modal-desc warn">
            Цю дію не можна скасувати. Розділ зникне з БД і з Bunny.
          </p>
          {#if deleteError}<div class="error">{deleteError}</div>{/if}
        </div>
        <div class="modal-actions">
          <button
            type="button"
            class="modal-btn modal-btn-secondary"
            disabled={moving}
            onclick={() => (deleteFinalStep = false)}
          >Назад</button>
          <button
            type="button"
            class="modal-btn modal-btn-primary"
            disabled={moving}
            onclick={moveAndDelete}
          >{moving ? 'Видаляю…' : 'Так, видалити'}</button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236, 0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-delete,
  .header-preview {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    color: var(--text-light);
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-preview {
    background: rgba(251, 246, 236, 0.15);
    border: 1px solid rgba(251, 246, 236, 0.25);
    margin-right: 4px;
  }
  .header-preview:hover { background: rgba(251, 246, 236, 0.25); }
  .header-preview svg { color: var(--text-light); display: block; }
  .header-delete {
    background: rgba(168, 80, 47, 0.18);
    border: 1px solid rgba(168, 80, 47, 0.35);
  }
  .header-delete:hover { background: rgba(168, 80, 47, 0.3); }
  .header-delete:disabled { opacity: 0.5; cursor: not-allowed; }
  .header-delete svg { color: var(--text-light); display: block; }

  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 12px 0 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    margin-bottom: 4px;
  }
  .field:focus { border-color: var(--outline); }
  textarea.field { resize: vertical; min-height: 100px; }
  .icon-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .name-input { flex: 1; margin-bottom: 0; }

  .status-pills {
    display: flex;
    gap: 6px;
    margin: 4px 0 8px;
  }
  .status-pill {
    flex: 1;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text-muted);
    padding: 9px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .status-pill.active {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }
  .info-line {
    margin: 12px 0 4px;
    font-size: 12px;
    color: var(--text-muted);
  }
  .error {
    margin: 12px 0;
    padding: 10px 14px;
    background: var(--danger-bg);
    color: var(--status-inactive);
    border: 1px solid var(--danger-border);
    border-radius: 10px;
    font-size: 12px;
  }
  .submit {
    width: 100%;
    margin-top: 18px;
    padding: 14px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .placeholder {
    padding: 30px 20px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
  }
  .placeholder.small { padding: 12px; font-size: 12px; border: 1px dashed var(--border2); border-radius: 10px; background: var(--bg2); }

  /* Modal */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000;
  }
  .modal-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 85vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up {
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); }
  .modal-close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg2);
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-body {
    padding: 14px 18px 18px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }
  .modal-desc {
    margin: 0 0 14px;
    font-size: 13px;
    color: var(--text);
    line-height: 1.5;
  }
  .mat-list {
    list-style: none;
    padding: 0;
    margin: 0 0 14px;
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--bg2);
  }
  .mat-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    font-size: 12px;
  }
  .mat-item:last-child { border-bottom: none; }
  .mat-icon { font-size: 14px; flex-shrink: 0; }
  .mat-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .draft-pill {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    background: var(--warn-bg);
    color: var(--status-expiring);
    padding: 2px 6px;
    border-radius: 999px;
  }

  .modal-actions {
    display: flex;
    gap: 8px;
    padding: 12px 18px calc(16px + env(safe-area-inset-bottom));
    border-top: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-btn {
    flex: 1;
    border: none;
    padding: 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .modal-btn-secondary {
    background: var(--bg2);
    color: var(--text);
    border: 1px solid var(--border2);
  }
  .modal-btn-primary {
    background: linear-gradient(135deg, var(--status-inactive), var(--status-inactive));
    color: var(--text-light);
  }
  .modal-btn-warn {
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
  }
  .modal-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .modal-desc.warn {
    color: var(--status-expiring);
    background: var(--warn-bg);
    border: 1px solid var(--warn-border);
    padding: 10px 12px;
    border-radius: 10px;
  }

  /* === Preview modal === */
  .preview-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 92vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  /* Mirror SectionPage layout so admin sees a faithful preview. */
  .preview-page { padding: 0; }
  .preview-topbar {
    padding: 12px 18px;
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    text-align: center;
  }
  .preview-content { padding: 16px 16px 100px; }
  .preview-content .promo {
    padding: 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 16px;
  }
  .preview-content .promo-text {
    font-size: 13px;
    line-height: 1.55;
    color: var(--text);
  }
  .preview-content .materials { display: flex; flex-direction: column; }
  .preview-content .material {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 8px;
  }
  .preview-content .material-icon {
    font-size: 20px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    border-radius: 8px;
    flex-shrink: 0;
  }
  .preview-content .material-text {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .preview-content .material-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .preview-content .material-draft {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    background: var(--warn-bg);
    color: var(--status-expiring);
    padding: 2px 6px;
    border-radius: 999px;
  }
  .preview-content .material-chevron {
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
  }
  .preview-empty {
    text-align: center;
    padding: 30px 20px;
    color: var(--text-muted);
    font-size: 13px;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
  }
  .preview-state-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
  }
  .preview-state-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--bg2);
    color: var(--text-muted);
    border: 1px solid var(--border);
  }
  .preview-state-pill.pub {
    background: var(--green-light);
    color: var(--green);
    border-color: var(--green-light);
  }
  .preview-state-pill.neutral {
    background: var(--pu-light);
    color: var(--accent-text);
    border-color: transparent;
  }
</style>
